import {
  IEmailBlock,
  IEmailLocaleContent,
  IEmailTemplateDetail,
} from "../../interfaces/emailTemplate.interface";

const canonical = (value: unknown): string =>
  JSON.stringify(value ?? null, (_, val) =>
    val && typeof val === "object" && !Array.isArray(val)
      ? Object.keys(val as Record<string, unknown>)
          .sort()
          .reduce(
            (acc, key) => {
              acc[key] = (val as Record<string, unknown>)[key];
              return acc;
            },
            {} as Record<string, unknown>
          )
      : val
  );

export const contentEqual = (
  a?: IEmailLocaleContent,
  b?: IEmailLocaleContent
): boolean => canonical(a) === canonical(b);

const blockEqual = (a?: IEmailBlock, b?: IEmailBlock): boolean =>
  canonical(a) === canonical(b);

export const hasPendingDraft = (detail: IEmailTemplateDetail): boolean => {
  const locales = Array.from(
    new Set([...Object.keys(detail.published), ...Object.keys(detail.draft)])
  );
  return locales.some(
    (loc) => !contentEqual(detail.published[loc], detail.draft[loc])
  );
};

export type BlockChange = "added" | "removed" | "changed" | "unchanged";

export interface BlockDiffRow {
  status: BlockChange;
  published?: IEmailBlock;
  draft?: IEmailBlock;
}

export interface LocaleDiff {
  locale: string;
  changed: boolean;
  subjectChanged: boolean;
  publishedSubject: string;
  draftSubject: string;
  rows: BlockDiffRow[];
}

export const diffLocale = (
  locale: string,
  published?: IEmailLocaleContent,
  draft?: IEmailLocaleContent
): LocaleDiff => {
  const pub = published?.blocks ?? [];
  const dr = draft?.blocks ?? [];
  const rows: BlockDiffRow[] = [];
  const count = Math.max(pub.length, dr.length);
  for (let i = 0; i < count; i += 1) {
    const p = pub[i];
    const d = dr[i];
    if (!p && d) rows.push({ status: "added", draft: d });
    else if (p && !d) rows.push({ status: "removed", published: p });
    else if (!blockEqual(p, d))
      rows.push({ status: "changed", published: p, draft: d });
    else rows.push({ status: "unchanged", published: p, draft: d });
  }
  const subjectChanged = (published?.subject ?? "") !== (draft?.subject ?? "");
  return {
    locale,
    subjectChanged,
    publishedSubject: published?.subject ?? "",
    draftSubject: draft?.subject ?? "",
    rows,
    changed: subjectChanged || rows.some((row) => row.status !== "unchanged"),
  };
};

export const diffTemplate = (detail: IEmailTemplateDetail): LocaleDiff[] => {
  const locales = detail.locales.length
    ? detail.locales
    : Array.from(
        new Set([
          ...Object.keys(detail.published),
          ...Object.keys(detail.draft),
        ])
      );
  return locales.map((loc) =>
    diffLocale(loc, detail.published[loc], detail.draft[loc])
  );
};

export const blockSummary = (block?: IEmailBlock): string => {
  if (!block) return "";
  if (block.type === "list")
    return (block.items ?? []).map((item) => `• ${item}`).join("\n");
  if (block.type === "table")
    return (block.rows ?? [])
      .map((row) => `${row.label}: ${row.value}`)
      .join("\n");
  if (block.type === "button") return `[${block.text}](${block.href ?? ""})`;
  return block.text ?? "";
};
